import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { FormControl } from '@mui/material';
import { Operator } from '../../hooks/useUrlFilters';
import Autocomplete from '../Autocomplete';
import Button from '../Button';
import DropDown, { DropDownInputLabel } from '../DropDown';
import { FlexColumn, FlexSpace } from '../Flex';
import TextField from '../TextField';
import { UNSET_FILTER_VALUE, UNSET_FILTER_VALUE_LABEL } from '../../constants';
import { space } from '../../style/utils';

const Wrapper = styled(FlexSpace)`
  gap: ${space('sm')};
  width: 100%;
`;

type Option = { value: string; label: string };

export type FilterOption = {
  property: string;
  propertyLabel: string;
  options: Option[];
  operators?: Operator[];
  readOnlyOperator?: boolean;
  multiselect?: boolean;
  isSingularValue?: boolean;
  showSearch?: boolean;
};

type OnChangeEventVal = {
  property?: string;
  operator?: Operator;
  value?: string[];
};

export type OnSubmitEventVal = {
  index?: number;
  property: string;
  operator: Operator;
  prevValue?: string[];
  value: string[];
};

const operatorOptions: Record<Operator, Option> = {
  contains: { label: 'contains', value: 'contains' },
  equals: { label: 'is', value: 'equals' },
  greaterThan: { label: 'greater than', value: 'greaterThan' },
  greaterThanOrEqual: { label: 'at least', value: 'greaterThanOrEqual' },
  lessThan: { label: 'less than', value: 'lessThan' },
  lessThanOrEqual: { label: 'at most', value: 'lessThanOrEqual' },
  notContains: { label: 'does not contain', value: 'notContains' },
  notEquals: { label: 'is not', value: 'notEquals' },
  upstreamFrom: { label: 'upstream from', value: 'upstreamFrom' },
  downstreamFrom: { label: 'downstream from', value: 'downstreamFrom' },
};

export type Filter = {
  property: string;
  operator: Operator;
};

export type Props = {
  filterOptions: FilterOption[];
  defaultOperators?: Operator[];
  defaultReadOnlyOperator?: boolean;
  multiselect?: boolean;
  onChange?: (val: OnChangeEventVal) => void;
  onSubmit?: (val: OnSubmitEventVal) => void;
  disabled?: boolean;
  currentFilters?: Filter[];
  onClose?: () => void;
  resetOnClose?: boolean;
  initialProperty?: string;
  initialOperator?: Operator;
  initialValue?: string[];
  autoApplyFreeText?: boolean;
  showAddButton?: boolean;
  sortOptions?: boolean;
};

const FilterSelector = ({
  filterOptions,
  onChange,
  onSubmit,
  disabled,
  defaultOperators = ['equals', 'notEquals', 'contains', 'notContains'],
  defaultReadOnlyOperator,
  multiselect,
  currentFilters,
  onClose,
  resetOnClose,
  initialProperty,
  initialOperator,
  initialValue,
  autoApplyFreeText,
  showAddButton = false,
  sortOptions,
}: Props) => {
  const [property, setProperty] = useState<string | undefined>(initialProperty);
  const [operator, setOperator] = useState<Operator | undefined>(
    initialOperator,
  );
  const [value, setValue] = useState<string[]>(initialValue || []);
  const [valueFocused, setValueFocused] = useState(false);

  const properties = useMemo(
    () =>
      filterOptions.map((o) => ({
        value: o.property,
        label: o.propertyLabel,
      })),
    [filterOptions],
  );

  const selectedFilter = useMemo(
    () => filterOptions.find((o) => o.property === property),
    [filterOptions, property],
  );

  const currentPropertyOperators = useMemo(() => {
    const cur: Record<string, string[]> = {};
    currentFilters?.forEach((filter) => {
      cur[filter.property] = (cur[filter.property] || []).concat(
        filter.operator,
      );
    });
    return cur;
  }, [currentFilters]);

  const operators = useMemo(
    () =>
      (selectedFilter?.operators || defaultOperators).map((o) => {
        const option = operatorOptions[o];
        if (
          o === 'equals' &&
          selectedFilter?.isSingularValue &&
          currentPropertyOperators[selectedFilter?.property]?.includes(o)
        ) {
          return { ...option, disabled: true };
        }
        return option;
      }),
    [defaultOperators, selectedFilter, currentPropertyOperators],
  );

  const values = useMemo(
    () =>
      selectedFilter?.options?.map((o) => ({
        value: o.value,
        label:
          o.value === UNSET_FILTER_VALUE ? UNSET_FILTER_VALUE_LABEL : o.label,
      })) || [],
    [selectedFilter?.options],
  );

  const isOperatorReadOnly = useMemo(
    () =>
      (defaultReadOnlyOperator &&
        selectedFilter?.readOnlyOperator === undefined) ||
      selectedFilter?.readOnlyOperator,
    [defaultReadOnlyOperator, selectedFilter],
  );

  const isValid = property && operator && value.length !== 0;

  const containsOperator =
    operator === 'contains' || operator === 'notContains';

  const handleClose = useCallback(() => {
    onClose?.();
    if (resetOnClose) {
      setProperty('');
    }
  }, [onClose, resetOnClose]);

  const handleSubmit = useCallback(
    (newVal: string[]) => {
      onSubmit?.({
        property: property!,
        operator: operator!,
        prevValue: containsOperator ? [] : value,
        value: newVal,
      });
      handleClose();
    },
    [containsOperator, handleClose, onSubmit, operator, property, value],
  );

  const disableValueField = useMemo(
    () => !property || !operator || disabled,
    [property, operator, disabled],
  );

  const isMultiselect =
    selectedFilter?.multiselect != null
      ? selectedFilter?.multiselect
      : multiselect;

  const shouldShowAddButton = showAddButton || isMultiselect;

  const showSearch = selectedFilter?.showSearch ?? true;

  useEffect(() => {
    if (initialProperty && property === initialProperty) {
      return;
    }
    if (isOperatorReadOnly) {
      const selectedOperator =
        selectedFilter?.operators?.[0] || defaultOperators?.[0];
      setOperator(selectedOperator);
    } else {
      setOperator(undefined);
    }
    setValue([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter?.property, isOperatorReadOnly]);

  useEffect(() => {
    if (initialOperator && operator === initialOperator) {
      return;
    }
    setValue([]);
  }, [operator, initialOperator]);

  useEffect(() => {
    onChange?.({ property, operator, value });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property, operator, value]);

  useEffect(() => {
    if (disabled) {
      setProperty('');
    }
  }, [disabled]);

  const content = (
    <Wrapper>
      <FormControl fullWidth>
        <DropDownInputLabel disabled={disabled}>Property</DropDownInputLabel>
        <DropDown
          size="large"
          value={property}
          onChange={(val) => setProperty(val)}
          options={properties}
          disabled={disabled}
          sortOptions={sortOptions}
        />
      </FormControl>

      {isOperatorReadOnly ? (
        <TextField
          value={operators.find((o) => o.value === operator)?.label || ''}
          InputProps={{
            readOnly: true,
          }}
          disabled={!property || disabled}
        />
      ) : (
        <FormControl fullWidth>
          <DropDownInputLabel disabled={!property || disabled}>
            Relationship
          </DropDownInputLabel>
          <DropDown
            size="large"
            value={operator}
            onChange={(val) => setOperator(val)}
            options={operators}
            disabled={!property || disabled}
            sortOptions={false}
          />
        </FormControl>
      )}

      {containsOperator ? (
        <Autocomplete
          freeSolo
          fullWidth
          options={values.map((v) => v.value)}
          value={value?.[0] || ''}
          onKeyUp={(e: any) => {
            if (e.key === 'Enter' && isValid) {
              handleSubmit(value);
            }
          }}
          onInputChange={(_, newValue: string) => {
            const newVal = newValue?.trim() ? [newValue] : [];
            setValue(newVal);
            if (autoApplyFreeText) {
              handleSubmit(newVal);
            }
          }}
          open={valueFocused && value?.[0]?.length > 0}
          onFocus={() => setValueFocused(true)}
          onBlur={() => setValueFocused(false)}
          componentsProps={{ popper: { sx: { width: 'auto !important' } } }}
          disableClearable
        />
      ) : (
        <FormControl fullWidth>
          <DropDownInputLabel disabled={disableValueField}>
            Value
          </DropDownInputLabel>
          <DropDown
            selectedOnTop={false}
            size="large"
            value={isMultiselect ? value : value?.[0]}
            onChange={(val) => {
              const currVal = Array.isArray(val) ? val : [val];
              if (val) {
                setValue(currVal);
              } else {
                setValue([]);
              }
              if (!shouldShowAddButton) {
                handleSubmit(currVal);
              }
            }}
            options={values}
            disabled={disableValueField}
            multiple={isMultiselect}
            showSearch={showSearch}
          />
        </FormControl>
      )}
    </Wrapper>
  );

  if (shouldShowAddButton) {
    return (
      <FlexColumn>
        {content}
        <Button
          variant="contained"
          color="primary"
          disabled={!isValid || disabled}
          onClick={() => {
            onSubmit?.({
              property: property!,
              operator: operator!,
              prevValue: [],
              value: value,
            });
            handleClose();
          }}
          sx={{
            width: 'fit-content',
            alignSelf: 'flex-end',
            marginTop: space('lg'),
          }}
        >
          Add
        </Button>
      </FlexColumn>
    );
  }

  return content;
};

export default FilterSelector;
